import { useTranslation } from 'next-i18next'
import { useRouter } from 'next/router'
import { css } from '@emotion/react'
import { memo } from 'react'

import Link from '@/components/common/Link'
import { mediaUp, mediaDown } from '@/styles/media'
import { colors, weights } from '@/styles/theme'
import { Text2Styles } from '@/styles/typography'
import { focusVisible, underlineHover } from '@/styles/mixins'
import { useTowns } from '@/service/apiHooks/parkingQueries'
import { TownResponse } from '@/types/api/town-and-airports'
import { useNavigationTracking } from '@/hooks/useNavigationTracking'
import { useMarket } from '@/utils/multi-markets/context'

import { getArrowIcon } from '@/constants/arrowAssets'

import Submenu from './Submenu'

interface Props {
  click: boolean
  hover: boolean
}

function ParkingsSubmenu(props: Props) {
  const { t } = useTranslation('common')
  const { hover = false, click = false } = props
  const { push } = useRouter()
  const { trackClick } = useNavigationTracking()
  const { locale, market } = useMarket()
  const region = market
  const { data: townsData } = useTowns(locale, region)
  const towns = townsData?.towns || []

  const handleNavigation = async (route: any) => {
    try {
      await push(route)
    } catch (e) {
      console.error(e)
    }
  }

  const handleTownClick = (town: TownResponse): void => {
    const route = {
      pathname: '/parking-search',
      query: {
        lat: town.lat,
        lon: town.lon,
        locationLabel: town.name,
        radius: 50000,
        category: town.cityUrlSlug
      }
    }
    trackClick(`menu_desktop_search_town_${town.name}`)
    handleNavigation(route)
  }

  const getTownHref = (town: TownResponse): string => {
    const params = new URLSearchParams({
      lat: town.lat?.toString() || '0',
      lon: town.lon?.toString() || '0',
      locationLabel: town.name || '',
      radius: '50000',
      category: town.cityUrlSlug || ''
    })
    return `/parking-search?${params.toString()}`
  }

  return (
    <Submenu
      mainLinkContent={
        <Link href={{ pathname: '/parking-search' }} passHref prefetch={false}>
          <a
            onClick={() => trackClick('menu_desktop_search_towns_all')}
            css={[mainLinkItem]}
            aria-label={t(
              'navigation.showAllTownsAccessible' as keyof typeof t
            )}
          >
            {t('navigation.showAllTowns')}
            <img
              src={getArrowIcon('arrow')}
              alt="Arrow icon"
              aria-hidden="true"
            />
          </a>
        </Link>
      }
      linksContent={
        <ul css={content} role="menu">
          {towns
            ?.sort((town, nextTown) => town?.name?.localeCompare(nextTown.name))
            .map((city, index) => (
              <li css={link} key={`town-${city.name}-${index}`} role="menuitem">
                <a
                  href={getTownHref(city)}
                  onClick={e => {
                    e.preventDefault()
                    handleTownClick(city)
                  }}
                  css={[linkItem]}
                  aria-label={t('navigation.searchInCity' as keyof typeof t, {
                    city: city.name
                  })}
                >
                  {city.name}
                </a>
              </li>
            ))}
        </ul>
      }
      click={click}
      hover={hover}
    />
  )
}

const mainLinkItem = css`
  ${focusVisible};
  ${Text2Styles};
  ${underlineHover};
  font-weight: ${weights.black};
  color: ${colors.white};
  margin-top: 18px;
  display: flex;
  align-items: center;
  cursor: pointer;

  svg {
    margin-inline-start: 4px;

    ${mediaDown('lg')} {
      width: 9px;
      height: 9px;
    }

    html[lang^='ar'] & {
      transform: rotate(180deg);
    }
  }

  ${mediaDown('lg')} {
    font-size: 12px;
  }

  ${mediaUp('lg')} {
    margin-top: 0;
  }
`

const content = css`
  list-style: none;
  display: flex;
  flex-direction: column;
  align-items: center;
  padding-bottom: 5px;

  ${mediaUp('lg')} {
    padding: 20px;
    padding-top: 0;
    align-items: flex-start;
  }
`

const link = css`
  display: flex;
  margin-top: 12px;

  ${mediaUp('lg')} {
    margin-top: 20px;
  }
`

const linkItem = css`
  ${focusVisible};
  ${Text2Styles};
  ${underlineHover};
  font-weight: ${weights.black};
  color: ${colors.white};
  line-height: normal;
  white-space: nowrap;
  background: none;
  border: none;
  padding: 0;
  text-align: left;
  cursor: pointer;

  ${mediaDown('lg')} {
    font-size: 12px;
  }

  ${mediaUp('lg')} {
    color: ${colors.twilightBlue};
    line-height: normal;
  }
`

export default memo(ParkingsSubmenu)
